import { useState, useEffect, useRef } from 'react'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import { alpha } from '@mui/material/styles'
import api from 'services/api'
import SplitPanes, {
  opensDetail,
  useOpenItem,
} from 'components/core/SplitPanes'
import LedgerTable, { OutcomeChip, routerHeardPath } from './LedgerTable'
import MomentCard from './MomentCard'
import { usePlayer, PlayerAudio, TraceDialog } from './MomentsBrowser'
import { Section, Line } from './MomentsSplit'
import FilterHeader from './FilterHeader'
import { CostTable, SpanTiming, spanCostLine, spanTimingLine } from './SpanCost'
import { MOMENT_OUTCOMES } from './vocabulary'

// The Moments page's other view (2026-10-21; it was a table under the
// moments): the spans the selector read, per span (step 4a), and what became
// of each moment it found — cut, refused by the duel, fenced by an overlap,
// not quotable, capped, or still owed. The trial's main question is the spans
// that made nothing, and a refused moment exists only here.
//
// The same two panes as the moments: the spans newest first on the left, the
// picked span's ledger on the right, in folds that carry from one span to the
// next — what it found (open at the start), what it cost, how long it took. A
// cut row opens its card in place. Where the caller may hear the span, it
// links to Stream Ingest's "What the router heard", which has its transcript
// and minutes and plays every found moment.

// The server's read takes a limit and no cursor: the newest 50 are the list.
const LIMIT = 50

const OPEN_AT_START = { found: true, cost: false, timing: false }

const spanKey = (row) => `${row.user_id}-${row.span_start_ms}`

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

const when = (startMs, endMs) =>
  `${dayjs(startMs).format('MMM D HH:mm')}${
    endMs ? ` · ${Math.round((endMs - startMs) / 60000)} min` : ''
  }`

// The span's spend: `≥` when the transcript doc is gone and only the
// selection's is counted.
const Cost = ({ row }) => {
  if (row.cost_usd == null) return '—'
  const text = `${row.transcript_missing ? '≥ ' : ''}$${row.cost_usd.toFixed(
    4
  )}`
  if (!row.transcript_missing) return text
  return (
    <Tooltip title="No transcript doc (they expire after 30 days): transcription is not counted.">
      <span>{text}</span>
    </Tooltip>
  )
}

const Account = ({ userId, username, onUser }) => (
  <Tooltip title={`Only this account · ${userId}`}>
    <Box
      component="span"
      data-row-action
      onClick={() => onUser({ id: userId, username })}
      sx={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
    >
      {username || userId.slice(-6)}
    </Box>
  </Tooltip>
)

const Outcomes = ({ outcomes }) =>
  Object.keys(outcomes).length === 0 ? (
    <Line>none found</Line>
  ) : (
    MOMENT_OUTCOMES.filter((o) => outcomes[o.key]).map((o) => (
      <OutcomeChip key={o.key} outcome={o.key} count={outcomes[o.key]} />
    ))
  )

const SpanRow = ({ row, picked, onPick, onUser }) => {
  const ref = useRef(null)
  // The arrow keys move the pick past the edge of the view; follow it.
  useEffect(() => {
    if (picked) ref.current?.scrollIntoView({ block: 'nearest' })
  }, [picked])
  return (
    <Paper
      ref={ref}
      variant="outlined"
      onClick={onPick}
      sx={(theme) => ({
        p: 1.5,
        borderRadius: 2,
        cursor: 'pointer',
        ...(picked
          ? {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
            }
          : { '&:hover': { backgroundColor: theme.palette.action.hover } }),
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }}>
          {when(row.span_start_ms, row.span_end_ms)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <Cost row={row} />
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" component="div">
        <Account userId={row.user_id} username={row.username} onUser={onUser} />{' '}
        · {row.state}
        {row.reason ? ` · ${row.reason}` : ''}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          flexWrap: 'wrap',
          alignItems: 'center',
          mt: 0.75,
        }}
      >
        <Outcomes outcomes={row.outcomes} />
      </Box>
    </Paper>
  )
}

// A cut row's card, fetched by id: the moments view may not hold it (it pages
// by 30 and filters by state and search).
const LedgerCard = ({ momentId, player, onTrace, onUser, mediaMode }) => {
  const [moment, setMoment] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    let live = true
    api.streamMetrics
      .getMoment({ momentId })
      .then((data) => live && setMoment(data))
      .catch((err) => live && setError(err.message))
    return () => {
      live = false
    }
  }, [momentId])
  if (error) {
    return (
      <Alert severity="info">
        {error === 'no such moment' ? 'The card was deleted.' : error}
      </Alert>
    )
  }
  if (!moment) return <CircularProgress size={18} />
  return (
    <MomentCard
      moment={moment}
      player={player}
      onTrace={onTrace}
      onUser={onUser}
      mediaMode={mediaMode}
    />
  )
}

// What the selector found, on its fold: how many, and what became of them.
const foundLine = (job) => {
  if (!job.moments.length) {
    return job.selected ? 'nothing found' : 'not selected yet'
  }
  const tally = MOMENT_OUTCOMES.map((o) => [
    o.label,
    job.moments.filter((row) => (row.outcome || 'owed') === o.key).length,
  ])
    .filter(([, n]) => n)
    .map(([label, n]) => `${label} ${n}`)
    .join(' · ')
  return `${job.moments.length} found · ${tally}`
}

// The pane is keyed by the span, so a pick reads its own ledger afresh.
const SpanDetail = ({ row, cardProps, onUser, open, onToggle }) => {
  const [ledger, setLedger] = useState(null)
  const [error, setError] = useState(null)
  const [trace, setTrace] = useState(null)
  useEffect(() => {
    let live = true
    api.streamMetrics
      .getSelectionSpan({ userId: row.user_id, spanStartMs: row.span_start_ms })
      .then((data) => live && setLedger(data))
      .catch((err) => live && setError(err.message))
    return () => {
      live = false
    }
  }, [row.user_id, row.span_start_ms])
  const openTrace = () =>
    api.streamMetrics
      .getSelectionTrace({
        userId: row.user_id,
        spanStartMs: row.span_start_ms,
      })
      .then(setTrace)
      .catch((err) => setError(err.message))
  const job = ledger?.job
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {/* On a phone the buttons wrap under the title rather than
            squeezing it. */}
        <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {when(row.span_start_ms, row.span_end_ms)}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            <Account
              userId={row.user_id}
              username={row.username}
              onUser={onUser}
            />
            {job && (
              <>
                {' '}
                · {job.state} · {plural(job.claims, 'claim')} · attempts{' '}
                {job.attempts}
                {job.reason ? ` · ${job.reason}` : ''}
                {job.last_error ? ` · last error: ${job.last_error}` : ''}
                {job.retry_at
                  ? ` · retry ${dayjs(job.retry_at).format('HH:mm')}`
                  : ''}
              </>
            )}
          </Typography>
        </Box>
        <Button
          size="small"
          disabled={!job}
          onClick={openTrace}
          sx={{ textTransform: 'none' }}
        >
          Trace
        </Button>
        {/* Only where the server says the caller may hear the span: their own,
            or anyone's on develop (STREAM_ADMIN_PLAYBACK). */}
        {ledger?.playable && (
          <Button
            size="small"
            component={Link}
            to={routerHeardPath({
              userId: ledger.user_id,
              username: ledger.username,
              spanStartMs: ledger.span_start_ms,
            })}
            sx={{ textTransform: 'none' }}
          >
            What the router heard
          </Button>
        )}
      </Box>
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error === 'no such span'
            ? 'The span’s ledger is gone: span rows live 90 days.'
            : error}
        </Alert>
      )}
      {!error && !ledger && (
        <Box sx={{ mt: 1.5 }}>
          <CircularProgress size={18} />
        </Box>
      )}
      {ledger && !job && (
        <Box sx={{ mt: 1.5 }}>
          <Line>No selection job yet.</Line>
        </Box>
      )}
      {job && (
        <Box sx={{ mt: 1.5 }}>
          <Section
            title="Found"
            summary={<Line>{foundLine(job)}</Line>}
            open={open.found}
            onToggle={() => onToggle('found')}
          >
            {job.moments.length === 0 ? (
              <Line>
                {job.selected
                  ? 'The selector found nothing worth a card.'
                  : 'Nothing selected: ' + (job.reason || 'not reached yet')}
              </Line>
            ) : (
              <LedgerTable
                rows={job.moments}
                renderCard={(momentId) => (
                  <LedgerCard momentId={momentId} {...cardProps} />
                )}
              />
            )}
          </Section>
          <Section
            title="Cost"
            summary={<Line>{spanCostLine(ledger)}</Line>}
            open={open.cost}
            onToggle={() => onToggle('cost')}
          >
            <CostTable ledger={ledger} />
          </Section>
          <Section
            title="Timing"
            summary={<Line>{spanTimingLine(ledger)}</Line>}
            open={open.timing}
            onToggle={() => onToggle('timing')}
          >
            <SpanTiming ledger={ledger} />
          </Section>
        </Box>
      )}
      <Dialog
        open={Boolean(trace)}
        onClose={() => setTrace(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Selection trace</DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{ fontSize: 12, whiteSpace: 'pre-wrap', m: 0 }}
          >
            {trace && JSON.stringify(trace, null, 2)}
          </Box>
        </DialogContent>
      </Dialog>
    </Paper>
  )
}

const SelectionSpans = ({ title, account, user, onUser }) => {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState(null)
  const [pickedKey, setPickedKey] = useState(null)
  const [shown, setShown] = useState(false)
  const openItem = useOpenItem()
  const [open, setOpen] = useState(OPEN_AT_START)
  const [traceId, setTraceId] = useState(null)
  // What a cut card's media chip says depends on the rule's mode, which only
  // the moments read reports: one row of it, read once.
  const [mediaMode, setMediaMode] = useState(undefined)
  const player = usePlayer()
  const userId = user?.id || null

  useEffect(() => {
    let live = true
    setRows(null)
    api.streamMetrics
      .getSelectionSpans({ userId, limit: LIMIT })
      .then((data) => {
        if (!live) return
        setRows(data.spans)
        setError(null)
      })
      .catch((err) => live && setError(err.message))
    return () => {
      live = false
    }
  }, [userId])

  useEffect(() => {
    api.streamMetrics
      .getMoments({ limit: 1 })
      .then((data) => setMediaMode(data.media?.mode))
      .catch(() => {})
  }, [])

  const list = rows || []
  // A pick the list no longer holds (another account) falls back to the top
  // row, and on a phone back to the list.
  const picked = list.find((row) => spanKey(row) === pickedKey) || list[0]
  const index = picked ? list.indexOf(picked) : -1
  const move = (delta) => {
    const next = list[index + delta]
    if (next) setPickedKey(spanKey(next))
  }
  const step = (event) => {
    if (event.defaultPrevented || !picked) return
    const delta = { ArrowDown: 1, ArrowUp: -1 }[event.key]
    if (!delta) return
    event.preventDefault()
    move(delta)
  }
  const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }))
  const made = list.filter((row) => row.outcomes.cut).length

  const header = (
    <>
      <FilterHeader title={title} account={account} active={user ? 1 : 0} />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          mb: 2,
        }}
      >
        {!rows && !error && <CircularProgress size={18} />}
        {rows && (
          <Typography variant="caption" color="text.secondary">
            {plural(rows.length, 'span')} a selection pass touched
            {rows.length === LIMIT ? ' (the newest 50)' : ''}, newest first ·{' '}
            {made} made a card · open one for what the selector picked and what
            the judge said
          </Typography>
        )}
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
    </>
  )

  return (
    <>
      <SplitPanes
        header={header}
        shown={shown && list.some((row) => spanKey(row) === pickedKey)}
        onBack={() => setShown(false)}
        onStep={move}
        position={{ index, count: list.length }}
        backLabel="Spans"
        list={
          <>
            {rows && !rows.length && (
              <Typography variant="body2" color="text.secondary" sx={{ my: 3 }}>
                No span has been selected yet
                {user ? ' for this account' : ''}.
              </Typography>
            )}
            <Box
              tabIndex={0}
              onKeyDown={step}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              {list.map((row) => (
                <SpanRow
                  key={spanKey(row)}
                  row={row}
                  picked={row === picked}
                  onPick={(event) => {
                    setPickedKey(spanKey(row))
                    if (opensDetail(event)) {
                      openItem()
                      setShown(true)
                    }
                  }}
                  onUser={onUser}
                />
              ))}
            </Box>
          </>
        }
        detail={
          picked && (
            <SpanDetail
              key={spanKey(picked)}
              row={picked}
              cardProps={{ player, onTrace: setTraceId, onUser, mediaMode }}
              onUser={onUser}
              open={open}
              onToggle={toggle}
            />
          )
        }
      />
      <PlayerAudio player={player} />
      <TraceDialog momentId={traceId} onClose={() => setTraceId(null)} />
    </>
  )
}

export default SelectionSpans
